import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { ActivityQuery, queries, useAuthConfig } from "@theprelude/core";
import { Truncate } from "@theprelude/ds";
import { ExternalLink } from "@/components/external-link";
import { SelectTechniqueIcon } from "@/components/overlay-icons";
import {
  benchmarkColumn,
  expectedResultColumn,
  odpColumns,
} from "@/components/table/columns";
import { Unavailable } from "@/components/table/unavaliable";
import {
  ExpectedResult,
  getExpectedResult,
  getPersonalPercentages,
  isActivityPresent,
} from "@/lib/odp";
import QuickViewTable from "./quickview-table";

interface QuickViewTechniques {
  tcode: string;
  name?: string;
  observed?: number;
  protected?: number;
  detected?: number;
  benchmark?: number;
  expectedResult?: ExpectedResult;
}
const columnHelper = createColumnHelper<QuickViewTechniques>();

const columns = [
  columnHelper.accessor("tcode", {
    enableSorting: true,
    cell: (info) => (
      <div className="flex items-center gap-1">
        {info.getValue()}
        <ExternalLink
          href={`https://attack.mitre.org/techniques/${info
            .getValue()
            .split(".")
            .join("/")}`}
        />
      </div>
    ),
    size: 120,
    meta: {
      label: "ID",
    },
  }),
  columnHelper.accessor("name", {
    enableSorting: true,
    sortUndefined: -1,
    cell: (info) => {
      const name = info.getValue();
      if (name === undefined) {
        return <Unavailable />;
      }

      return (
        <SelectTechniqueIcon id={info.row.original.tcode}>
          <Truncate text={name}>{name}</Truncate>
        </SelectTechniqueIcon>
      );
    },
    size: 100,
    meta: {
      label: "Name",
    },
  }),
  ...odpColumns(columnHelper),
  columnHelper.accessor("expectedResult", expectedResultColumn),
  columnHelper.accessor("benchmark", benchmarkColumn),
];

export function TechniquesTable({ query }: { query: ActivityQuery }) {
  const authConfig = useAuthConfig();

  const techniquesQuery = useQuery({
    ...queries.detect.activity.techniques(authConfig, query),
    select: (activity) =>
      activity
        .filter((a) => isActivityPresent(a.personal))
        .map((a) => {
          return {
            tcode: a.id,
            name: a.name ?? undefined,
            benchmark: a.social ?? undefined,
            protected: a.protected ?? undefined,
            ...getPersonalPercentages(a.personal),
            expectedResult: getExpectedResult(a.expected, a?.personal),
          };
        }),
  });

  return (
    <QuickViewTable
      name="quickview-techniques"
      data={techniquesQuery.data ?? []}
      columns={columns}
      isLoading={techniquesQuery.isLoading}
    />
  );
}
